import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import { formatAddress } from 'localized-address-format'

import { YStack, XStack, styled } from 'tamagui'
import { Icon, Paragraph, Link, ExternalLink } from '@newComponents'

import {
  EventScreenJoin_event$key,
  EventScreenJoin_event$data,
} from './__generated__/EventScreenJoin_event.graphql'
import {
  EventScreenJoin_query$data,
  EventScreenJoin_query$key,
} from './__generated__/EventScreenJoin_query.graphql'

export const EventScreenJoin = ({
  event,
  query,
}: {
  event: EventScreenJoin_event$key
  query: EventScreenJoin_query$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenJoin_event on Event {
        hasVenue
        hasExternalUrl

        externalUrl

        myAttendance {
          status
        }

        venue {
          name
          entryInstructions

          address {
            addressLine1
            addressLine2
            city
            stateProvinceRegionCode
            zipPostalCode
            countryCode

            formattedAddress
            location {
              geojson
            }
          }
        }

        owner {
          ... on Guild {
            myMembership {
              membershipLevel
            }
          }

          ... on User {
            isMe
          }
        }
      }
    `,
    event
  )

  const viewerData = useFragment(
    graphql`
      fragment EventScreenJoin_query on Query {
        viewer {
          authorizationLevel
        }
      }
    `,
    query
  )

  return (
    <>
      <InPerson data={data} viewerData={viewerData} />

      <Online data={data} viewerData={viewerData} />
    </>
  )
}

const InPerson = ({
  data,
  viewerData,
}: {
  data: EventScreenJoin_event$data
  viewerData: EventScreenJoin_query$data
}) => {
  const formattedAddress = useMemo(() => {
    if (!data.venue?.address) {
      return
    }

    if (data.venue.address.formattedAddress) {
      return [data.venue.address.formattedAddress]
    }

    return formatAddress({
      addressLines: [
        data.venue.address?.addressLine1,
        data.venue.address?.addressLine2,
      ].filter((line) => !!line) as string[],
      locality: data.venue.address?.city ?? undefined,
      postalCode: data.venue.address?.zipPostalCode ?? undefined,
      administrativeArea: data.venue.address?.stateProvinceRegionCode ?? undefined,
      postalCountry: data.venue.address?.countryCode ?? undefined,
    })
  }, [data.venue?.address])

  const googleMapsURL = useMemo(() => {
    if (!formattedAddress || !data.venue?.name) {
      return
    }

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      [data.venue.name, ...formattedAddress].join(', ')
    )}`
  }, [formattedAddress, data.venue?.name])

  if (!data.hasVenue) {
    return null
  }

  if (
    data.venue &&
    formattedAddress &&
    googleMapsURL &&
    (data.myAttendance?.status === 'ATTENDING_IN_PERSON' ||
      viewerData.viewer?.authorizationLevel === 'ADMIN')
  ) {
    return (
      <ExternalLink
        href={googleMapsURL}
        hoverStyle={{ textDecorationLine: 'none' }}
        flex={1}
      >
        <XStack gap='$4' alignItems='flex-start'>
          <IconFrame>
            <Icon name='location' size='$7' color='$color' />
          </IconFrame>

          <YStack gap='$1' flex={1}>
            <Paragraph size='$5' fontWeight='$5'>
              {data.venue.name}
            </Paragraph>

            {!!data.venue.entryInstructions && (
              <Paragraph
                size='$4'
                fontWeight='$4'
                marginBottom='$4'
                maxWidth={200}
              >
                {data.venue.entryInstructions}
              </Paragraph>
            )}

            <YStack>
              {formattedAddress.map((addressPart, index) => {
                if (!addressPart) {
                  return null
                }

                return (
                  <Paragraph key={index} size='$4' fontWeight='$4'>
                    {addressPart}
                  </Paragraph>
                )
              })}
            </YStack>
          </YStack>
        </XStack>
      </ExternalLink>
    )
  }

  return (
    <XStack gap='$4' alignItems='center'>
      <IconFrame>
        <Icon name='location' size='$7' color='$color' />
      </IconFrame>

      <YStack flex={1}>
        <Paragraph size='$5' fontWeight='$5'>
          In-Person
        </Paragraph>

        {data.myAttendance?.status === 'WAITING_IN_PERSON' ? (
          <Paragraph size='$4' color='$colorMuted' fontWeight='$3'>
            Address available once confirmed
          </Paragraph>
        ) : (
          <Paragraph size='$4' color='$colorMuted' fontWeight='$3'>
            Address available to attendees
          </Paragraph>
        )}
      </YStack>
    </XStack>
  )
}

const Online = ({
  data,
  viewerData,
}: {
  data: EventScreenJoin_event$data
  viewerData: EventScreenJoin_query$data
}) => {
  if (!data.hasExternalUrl || !data.externalUrl) {
    return null
  }

  if (
    ['WAITING_IN_PERSON', 'ATTENDING_IN_PERSON', 'ATTENDING_VIRTUAL'].includes(
      data.myAttendance?.status || ''
    ) ||
    data.owner?.isMe ||
    ['EDITOR', 'ORGANIZER'].includes(
      data.owner?.myMembership?.membershipLevel || ''
    ) ||
    viewerData.viewer?.authorizationLevel === 'ADMIN'
  ) {
    return (
      <ExternalLink
        href={data.externalUrl}
        hoverStyle={{ textDecorationLine: 'none' }}
      >
        <XStack gap='$4' alignItems='center'>
          <IconFrame>
            <Icon name='video-person-sparkle' size='$7' color='$color' />
          </IconFrame>

          <YStack flex={1}>
            <Paragraph size='$4' color='$colorMuted' fontWeight='$3'>
              Join Online
            </Paragraph>

            <Paragraph
              size='$5'
              fontWeight='$5'
              color='$primaryColor'
              style={{ wordBreak: 'break-all' }}
              hoverStyle={{ textDecorationLine: 'underline' }}
            >
              {data.externalUrl}
            </Paragraph>
          </YStack>
        </XStack>
      </ExternalLink>
    )
  }

  return (
    <XStack gap='$4' alignItems='center'>
      <IconFrame>
        <Icon name='video-person-sparkle' size='$7' color='$color' />
      </IconFrame>

      <YStack>
        <Paragraph size='$5' fontWeight='$5'>
          Online
        </Paragraph>

        <Paragraph size='$4' color='$colorMuted' fontWeight='$3'>
          Link available to attendees
        </Paragraph>
      </YStack>
    </XStack>
  )
}

const IconFrame = styled(YStack, {
  width: '$10',
  height: '$10',

  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$background',
  borderRadius: '$2',

  // @ts-expect-error boxShadow is web-only, Tamagui token
  boxShadow: '$boxShadowLight',
})
